// Funds (PRD 83 Wave 2): the api's shapes for a business's funds, the switch
// that turns them on, and what the journal register and the reports carry
// once they are on. In their own file rather than the businessApi hub, which
// nearly every test imports — only the business payload's two fields
// (`fund_mode`, `default_fund_id`) and the framework's two live there.

import {
  BalanceSheetLiveReport,
  CreateJournalEntryParams,
  FundMode,
  ISODate,
  TrialBalanceRow,
  MoneyString,
  UUID,
} from './businessApi';

export type { FundMode } from './businessApi';

/** Restricted funds are the donor-restricted ones (PRD 83 §4.1). */
export type FundRestriction = 'unrestricted' | 'restricted';

export interface Fund {
  id: UUID;
  name: string;
  code: string | null;
  restriction: FundRestriction;
  /** Inactive: kept on every past line, never offered for a new one. */
  active: boolean;
  /** No line dated after this day may be posted to the fund. */
  closed_on: ISODate | null;
  /** The business's default fund: where every untagged line goes. */
  default: boolean;
}

export interface CreateFundParams {
  name: string;
  code?: string;
  restriction?: FundRestriction;
}

export interface UpdateFundParams {
  name?: string;
  code?: string | null;
  restriction?: FundRestriction;
  active?: boolean;
  /** null reopens a closed fund. */
  closed_on?: ISODate | null;
}

/** GET/PATCH fund-settings. */
export interface FundSettings {
  fund_mode: FundMode;
  default_fund_id: UUID | null;
  suggested_fund_mode: FundMode;
  /** The modes the api accepts today: ["off", "tracking"]. */
  available_fund_modes: FundMode[];
  default_fund_name: string;
}

export interface UpdateFundSettingsParams {
  fund_mode?: FundMode;
  default_fund_id?: UUID;
}

/** The error codes the fund endpoints answer with. */
export const FUND_ERROR_CODES = {
  /** A mode the api names but has not built (self_balancing). */
  notAvailable: 'not_available',
  /** Turning funds off once a posted line carries one. */
  fundsInUse: 'funds_in_use',
  /** Deleting a fund a posted line carries, or the default. */
  fundInUse: 'fund_in_use',
  validationFailed: 'validation_failed',
  /** A report or register fund filter the api could not read. */
  invalidFilter: 'invalid_fund_filter',
  forbidden: 'forbidden',
} as const;

// ---- The ledger ----

/** One fund an entry's lines carry, as the register lists them. */
export interface FundTag {
  id: UUID;
  name: string | null;
}

/** A register row once funds are on; absent while they are off. */
export interface FundTaggedJournalEntryWire {
  funds?: FundTag[];
}

/** A detail line once funds are on; both keys absent while they are off. */
export interface FundTaggedJournalLineWire {
  fund_id?: UUID | null;
  fund_name?: string | null;
}

/**
 * The JE create payload with funds: a header fund for every blank line, and
 * a line's own. Either is optional — a blank line takes the header's, else
 * the business default — so a fund is never required.
 */
export type CreateJournalEntryWithFunds = Omit<CreateJournalEntryParams, 'lines'> & {
  fund_id?: UUID;
  lines: (CreateJournalEntryParams['lines'][number] & { fund_id?: UUID })[];
};

// ---- Reports ----

/**
 * One fund filter on the register or the P&L. No filter is `null`, never an
 * empty selection; ids and "unassigned only" never combine.
 */
export type ReportFundSelection =
  | { mode: 'ids'; ids: UUID[] }
  | { mode: 'unassigned' };

/** Mixed into a query: absent or null sends no fund params at all. */
export interface FundFilterQuery {
  fund?: ReportFundSelection | null;
}

/** What a fund-filtered P&L echoes back; absent on an unfiltered one. */
export interface FundFilterEcho {
  fund_ids: UUID[];
  unassigned: boolean;
}

export interface FundFilteredReportWire {
  fund_filter?: FundFilterEcho;
}

/** One column of the trial balance by fund. */
export interface TrialBalanceFundColumn {
  /** A fund id, or "unassigned" for lines posted before funds were on. */
  key: string;
  name: string;
  rows: TrialBalanceRow[];
  total_debits: MoneyString;
  total_credits: MoneyString;
  /** Under tracking a single fund need not balance: information, not an error. */
  balanced: boolean;
}

/** GET reports/trial-balance?group_by=fund */
export interface TrialBalanceByFundReport {
  as_of: ISODate;
  group_by: 'fund';
  columns: TrialBalanceFundColumn[];
}

/** The column key of the lines that carry no fund. */
export const UNASSIGNED_FUND_KEY = 'unassigned';

// ---- The nonprofit statements and release from restriction (PRD 83 Wave 3) ----

/**
 * GET reports/statement-of-financial-position: the balance sheet's shape,
 * with net assets in their classes as the equity section's lines. Never a
 * partial view. `note` says how much is unassigned (no fund), or is null.
 */
export type StatementOfFinancialPositionReport = Omit<BalanceSheetLiveReport, 'report'> & {
  report: 'statement_of_financial_position';
  note: string | null;
};

/** A net asset class's key, or "total". Every amounts map is keyed by these. */
export type StatementOfActivitiesAmounts = Record<string, MoneyString>;

export interface StatementOfActivitiesColumn {
  key: string;
  label: string;
}

export interface StatementOfActivitiesSection {
  key: 'revenue' | 'cost_of_goods_sold' | 'operating_expenses' | 'other_income' | 'other_expenses';
  name: string;
  type: string;
  lines: {
    gl_account_id: UUID;
    account_number: string | null;
    name: string;
    subtype: string | null;
    amounts: StatementOfActivitiesAmounts;
  }[];
  subtotals: StatementOfActivitiesAmounts;
}

/** GET reports/statement-of-activities: the P&L by net asset class, then Total. */
export interface StatementOfActivitiesReport {
  report: 'statement_of_activities';
  basis: 'accrual';
  from: ISODate;
  to: ISODate;
  currency: string;
  note: string | null;
  columns: StatementOfActivitiesColumn[];
  sections: StatementOfActivitiesSection[];
  totals: Record<
    | 'revenue'
    | 'cost_of_goods_sold'
    | 'gross_profit'
    | 'operating_expenses'
    | 'operating_income'
    | 'other_income'
    | 'other_expenses'
    | 'net_income',
    StatementOfActivitiesAmounts
  >;
  /** Below the P&L. `releases` sums to zero in Total. */
  net_assets: Record<'releases' | 'other_changes' | 'change' | 'beginning' | 'ending', StatementOfActivitiesAmounts>;
}

/** The column key of the statement's Total. */
export const STATEMENT_TOTAL_KEY = 'total';

/** One restricted fund on GET fund-releases/proposal. Every step is shown, not only the answer. */
export interface ReleaseProposalRow {
  fund_id: UUID;
  fund_name: string;
  qualifying_expense: MoneyString;
  released: MoneyString;
  outstanding: MoneyString;
  restricted_balance: MoneyString;
  proposed: MoneyString;
  /** The restricted balance capped the proposal below what was spent. */
  capped: boolean;
}

export interface ReleaseProposal {
  from: ISODate;
  to: ISODate;
  rows: ReleaseProposalRow[];
}

/** POST fund-releases. to_fund_id defaults to the business's default fund. */
export interface CreateReleaseParams {
  fund_id: UUID;
  amount: string;
  entry_date: ISODate;
  to_fund_id?: UUID;
  memo?: string;
}

export interface ReleaseCreated {
  journal_entry_id: UUID;
  entry_number: string | number;
  entry_date: ISODate;
  amount: MoneyString;
  memo: string;
}
